'use client';

import { QuestionMarkCircledIcon } from '@radix-ui/react-icons';

import { PropsWithChildren } from 'react';

import Sidebar from '@/app/[locale]/(legacy)/(auth)/_components/sidebar';
import Logo from '@/app/_components/header/components/logo';
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
} from '@/app/_components/ui/drawer';
import { cn } from '@/lib/utils';

interface LayoutProps extends PropsWithChildren {
  sideTitle: string;
  side: React.ReactNode;
  cta: React.ReactNode;
  back?: React.ReactNode;
  // Scope de re-skin (ex. `auth-v2`) posé par page — permet de repimper une page
  // (login d'abord) sans toucher aux autres pages qui partagent ce Layout.
  className?: string;
}

// Single DOM tree: `children` and `cta` are rendered exactly once.
// CSS controls the layout (sidebar on desktop, drawer on mobile).
// `side` is rendered twice (sidebar + drawer) but is stateless display content.
const Layout = ({
  sideTitle,
  side,
  cta,
  back,
  className,
  children,
}: LayoutProps) => {
  return (
    <div
      className={cn('flex h-screen flex-col lg:grid lg:grid-cols-5', className)}
    >
      {/* Sidebar — desktop only */}
      <div className="hidden lg:col-span-2 lg:block">
        <Sidebar title={sideTitle}>{side}</Sidebar>
      </div>

      {/* Right column — full page on mobile, col-span-3 on desktop */}
      <div className="flex flex-col lg:col-span-3">
        {/* Mobile header — hidden on desktop */}
        <header className="flex w-full shrink-0 justify-between border-b p-6 lg:hidden">
          {back ?? <span />}
          <Logo />
          <Drawer>
            <DrawerTrigger className="flex size-8 items-center justify-center rounded-full bg-primary text-white">
              <QuestionMarkCircledIcon className="size-6" />
            </DrawerTrigger>
            <DrawerContent className="flex flex-col gap-4 p-6 pb-20">
              <DrawerHeader>
                <h3 className="text-xl font-semibold">{sideTitle}</h3>
              </DrawerHeader>
              {side}
            </DrawerContent>
          </Drawer>
        </header>

        {/* Main content — single instance, rendered once */}
        <div className="flex flex-1 flex-col justify-center gap-8 overflow-auto p-4 lg:px-16 lg:py-8">
          {back && <div className="hidden lg:block">{back}</div>}
          {children}
          {/* On mobile: border-top mimics the original footer; on desktop: no border */}
          <div className="border-t pt-6 lg:border-0 lg:pt-0">{cta}</div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
